<template>
  <div ref="wrapRef" class="tenant-tree-select" :style="props.style">
    <a-tree-select
      v-model:value="checkedNodes"
      style="width: 100%"
      :tree-data="treeData"
      :allow-clear="allowClear"
      :placeholder="placeholder"
      :loading="loading"
      :max-tag-count="maxTagCount"
      tree-checkable
      tree-check-strictly
      show-search
      tree-node-filter-prop="title"
      popup-class-name="tenant-tree-select-dropdown"
      :dropdown-style="{ maxHeight: '360px', overflow: 'auto', zIndex: 999 }"
      :get-popup-container="getPopupContainer"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type CSSProperties } from 'vue'
import * as tenantAPI from '@/api/tenant'
import type { TenantTreeNode } from '@/api/tenant'

defineOptions({
  name: 'tenantTreeSelect',
})

type Props = {
  style?: CSSProperties
  allowClear?: boolean
  placeholder?: string
  maxTagCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  style: () => ({ width: '220px' }),
  allowClear: true,
  placeholder: '请选择租户',
  maxTagCount: 1,
})

const emit = defineEmits<{
  (e: 'change', value: string[]): void
}>()

const tenantIds = defineModel<string[]>('tenantIds', {
  default: () => [],
})

type TreeNode = {
  title: string
  value: string
  key: string
  children?: TreeNode[]
}

type CheckedNode = {
  value: string
  label: string
}

const loading = ref(false)
const treeData = ref<TreeNode[]>([])
const nodeMap = ref<Map<string, TreeNode>>(new Map())
const applying = ref(false)
const wrapRef = ref<HTMLElement | null>(null)

/** 挂到组件容器内，避免下拉层挂到 body 后盖住 sticky 顶栏 */
const getPopupContainer = () => wrapRef.value || document.body

const mapTree = (nodes: TenantTreeNode[] = []): TreeNode[] =>
  nodes.map((item) => {
    const value = String(item.tenantId)
    const mapped: TreeNode = {
      title: item.tenantName || value,
      value,
      key: value,
      children: item.children?.length ? mapTree(item.children) : undefined,
    }
    return mapped
  })

const collectNodes = (nodes: TreeNode[], map: Map<string, TreeNode>) => {
  for (const node of nodes) {
    map.set(node.value, node)
    if (node.children?.length) collectNodes(node.children, map)
  }
}

const collectDescendantIds = (node: TreeNode, acc: string[]) => {
  for (const child of node.children || []) {
    acc.push(child.value)
    collectDescendantIds(child, acc)
  }
}

const fetchTree = async () => {
  try {
    loading.value = true
    const res = await tenantAPI.queryTenantTree()
    const roots = Array.isArray(res.data) ? res.data : []
    const mapped = mapTree(roots)
    const map = new Map<string, TreeNode>()
    collectNodes(mapped, map)
    treeData.value = mapped
    nodeMap.value = map
  } catch (error) {
    console.error('获取租户树失败', error)
    treeData.value = []
    nodeMap.value = new Map()
  } finally {
    loading.value = false
  }
}

const applyParentChild = (nextIds: string[], prevIds: string[]) => {
  const next = new Set(nextIds)
  const prev = new Set(prevIds)
  for (const id of next) {
    if (prev.has(id)) continue
    const node = nodeMap.value.get(id)
    if (!node) continue
    const descendants: string[] = []
    collectDescendantIds(node, descendants)
    descendants.forEach((childId) => next.add(childId))
  }
  for (const id of prev) {
    if (next.has(id)) continue
    const node = nodeMap.value.get(id)
    if (!node) continue
    const descendants: string[] = []
    collectDescendantIds(node, descendants)
    descendants.forEach((childId) => next.delete(childId))
  }
  return Array.from(next)
}

const normalizeChecked = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (item && typeof item === 'object' && 'value' in item) {
        return String((item as CheckedNode).value)
      }
      return item == null ? '' : String(item)
    })
    .filter(Boolean)
}

const checkedNodes = computed({
  get: (): CheckedNode[] =>
    (tenantIds.value || []).map((id) => ({
      value: id,
      label: nodeMap.value.get(id)?.title || id,
    })),
  set: (value) => {
    if (applying.value) return
    const nextIds = normalizeChecked(value)
    const synced = applyParentChild(nextIds, tenantIds.value || [])
    applying.value = true
    tenantIds.value = synced
    applying.value = false
  },
})

const handleChange = (value: unknown) => {
  const nextIds = applyParentChild(normalizeChecked(value), tenantIds.value || [])
  applying.value = true
  tenantIds.value = nextIds
  applying.value = false
  emit('change', nextIds)
}

watch(
  tenantIds,
  (ids) => {
    if (!ids) tenantIds.value = []
  },
  { immediate: true }
)

onMounted(() => {
  void fetchTree()
})
</script>

<style lang="less">
.tenant-tree-select {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  overflow: visible;
}

/* 顶栏 z-index 为 1100，下拉层不得高于顶栏 */
.ant-select-dropdown.tenant-tree-select-dropdown {
  z-index: 999;
}
</style>
